import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CreditCard, Coins, Loader2, Package, Sparkles, HelpCircle, Send, CheckCircle } from 'lucide-react';
import { ScrollAnimateIn } from '../hooks/useScrollAnimation';
import { DevotionalBookletDialog } from '../components/DevotionalBookletDialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token, user, loading: authLoading, refreshUser } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [myFonts, setMyFonts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasingCredits, setPurchasingCredits] = useState(false);
  
  // Help form state
  const [helpEmail, setHelpEmail] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [helpSent, setHelpSent] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Pre-fill email if user is logged in
    if (user?.email) {
      setHelpEmail(user.email);
    }

    if (isAuthenticated) {
      // Refresh user data to get latest credits
      if (refreshUser) {
        refreshUser();
      }
      fetchData();
    }
  }, [isAuthenticated, authLoading, navigate, refreshUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [purchasesRes, fontsRes] = await Promise.all([
        axios.get(`${API_URL}/api/user/purchases`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/user/fonts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setPurchases(purchasesRes.data);
      setMyFonts(fontsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseCredits = async () => {
    setPurchasingCredits(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/credits/purchase`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Failed to purchase credits:', error);
      toast.error('Failed to start checkout');
    } finally {
      setPurchasingCredits(false);
    }
  };

  const handleSubmitHelp = (e) => {
    e.preventDefault();
    if (!helpMessage) {
      toast.error('Please enter a message');
      return;
    }
    
    // Create mailto link
    const subject = encodeURIComponent(`Support Request from ${helpEmail || user?.email || 'User'}`);
    const body = encodeURIComponent(`From: ${helpEmail || user?.email || 'Not provided'}\nUser: ${user?.name || 'Unknown'}\n\n${helpMessage}`);
    const mailtoLink = `mailto:pins@funtzguntz.com?subject=${subject}&body=${body}`;
    
    // Clear the form immediately
    const sentMessage = helpMessage;
    setHelpMessage('');
    setHelpSent(true);
    
    // Open email client in new window to keep user on page
    window.open(mailtoLink, '_blank');
    
    // Show success message
    toast.success('Your email client has been opened. Please send the message to complete your request!', {
      duration: 5000,
    });
    
    // Reset the sent state after a few seconds so they can send another message
    setTimeout(() => setHelpSent(false), 10000);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0D0507]">
        <Header />
        <main className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Gothic Dashboard Skeleton */}
            <div className="animate-gothic-fade space-y-8">
              {/* Header skeleton */}
              <div className="space-y-4">
                <div className="skeleton-text w-24 h-4" />
                <div className="skeleton-text w-72 h-12" />
              </div>
              
              {/* Stats grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 stagger-cathedral">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="skeleton-card p-6 h-32">
                    <div className="flex items-center gap-4">
                      <div className="skeleton-avatar w-12 h-12" />
                      <div className="space-y-2 flex-1">
                        <div className="skeleton-text w-16 h-3" />
                        <div className="skeleton-text w-12 h-8" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Help section skeleton */}
              <div className="skeleton-card p-8 h-64">
                <div className="space-y-4">
                  <div className="skeleton-text w-48 h-6" />
                  <div className="space-y-3">
                    <div className="skeleton-text w-full h-4" />
                    <div className="skeleton-text w-4/5 h-4" />
                    <div className="skeleton-text w-3/5 h-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0507]" data-testid="dashboard-page">
      <Header />
      
      <main className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <ScrollAnimateIn animation="fade-up" className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <span className="mono-label">Dashboard</span>
              <h1 
                className="text-4xl lg:text-5xl font-gothic tracking-wide mt-4 text-[#F4EFE6]"
              >
                Welcome, {user?.name}
              </h1>
            </div>
            <DevotionalBookletDialog token={token} />
          </ScrollAnimateIn>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            {/* Generation Credits Card */}
            <ScrollAnimateIn animation="fade-up" delay={0}>
              <div className="card-surface p-6 border-2 border-[#D4AF37]/40 rounded-lg h-full">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#722F37] rounded-lg">
                  <Coins className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="mono-label">Generation Credits</p>
                  <p className="text-3xl font-cinzel font-bold text-[#D4AF37]" data-testid="credits-count">
                    {user?.generation_credits ?? 0}
                  </p>
                </div>
              </div>
              {(user?.generation_credits ?? 0) <= 3 && (
                <Button
                  onClick={handlePurchaseCredits}
                  disabled={purchasingCredits}
                  size="sm"
                  className="w-full btn-primary rounded-lg"
                  data-testid="buy-credits-btn"
                >
                  {purchasingCredits ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Buy 50 Credits - $5
                </Button>
              )}
              {(user?.generation_credits ?? 0) > 3 && (
                <p className="text-xs text-[#C2B49A] font-serif">Each font generation uses 1 credit</p>
              )}
            </div>
            </ScrollAnimateIn>

            <ScrollAnimateIn animation="fade-up" delay={100}>
              <div className="card-surface p-6 rounded-lg h-full">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center gothic-border rounded-lg">
                    <Package className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="mono-label">Purchased Fonts</p>
                    <p className="text-3xl font-cinzel font-bold text-[#F4EFE6]">{purchases.length}</p>
                  </div>
                </div>
              </div>
            </ScrollAnimateIn>
            
            <ScrollAnimateIn animation="fade-up" delay={200}>
              <div className="card-surface p-6 rounded-lg h-full">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center gothic-border rounded-lg">
                    <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="mono-label">Created Fonts</p>
                    <p className="text-3xl font-cinzel font-bold text-[#F4EFE6]">{myFonts.length}</p>
                  </div>
                </div>
              </div>
            </ScrollAnimateIn>

            <ScrollAnimateIn animation="fade-up" delay={300}>
              <div className="card-surface p-6 rounded-lg h-full">
                <Button
                  onClick={() => navigate('/generator')}
                  className="w-full h-full btn-primary rounded-lg"
                  data-testid="create-new-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Create New Font
                </Button>
              </div>
            </ScrollAnimateIn>
          </div>

          {/* Help Section */}
          <ScrollAnimateIn animation="fade-up" delay={400} className="mt-12">
            <div className="card-surface p-8 rounded-lg">
              <div className="flex items-center gap-3 mb-6">
                <HelpCircle className="w-6 h-6 text-[#D4AF37]" />
                <h2 className="text-2xl font-cinzel text-[#F4EFE6]">
                  Need Help?
                </h2>
              </div>
              
              {helpSent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#2F7248]/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#2F7248]/40">
                    <CheckCircle className="w-8 h-8 text-[#4ADE80]" />
                  </div>
                  <h3 className="text-xl font-cinzel font-semibold text-[#F4EFE6] mb-2">Email Client Opened!</h3>
                  <p className="text-[#C2B49A] mb-4 font-serif">
                    Please send the message from your email app to complete your request.
                  </p>
                  <Button
                    onClick={() => setHelpSent(false)}
                    variant="outline"
                    className="btn-secondary rounded-lg"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-[#C2B49A] mb-6 font-serif">
                    Have a question or issue? Send us a message and we will get back to you as soon as possible.
                  </p>
                  <form onSubmit={handleSubmitHelp} className="space-y-4">
                    <div>
                      <label className="mono-label block mb-2">Your Email</label>
                      <input
                        type="email"
                        value={helpEmail}
                        onChange={(e) => setHelpEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="input-terminal w-full rounded-lg"
                        required
                        data-testid="help-email-input"
                      />
                    </div>
                    <div>
                      <label className="mono-label block mb-2">Message</label>
                      <textarea
                        value={helpMessage}
                        onChange={(e) => setHelpMessage(e.target.value)}
                        placeholder="Describe your issue or question..."
                        rows={4}
                        className="input-terminal w-full rounded-lg resize-none"
                        required
                        data-testid="help-message-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={!helpMessage}
                      className="btn-primary px-6 py-3 rounded-lg"
                      data-testid="help-submit-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                  </form>
                </>
              )}
            </div>
          </ScrollAnimateIn>
        </div>
      </main>
    </div>
  );
}
